import React, { Suspense } from 'react';
import styled from 'styled-components';
import { T } from '@transifex/react';

import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { Sidebar } from '../layout/sidebar';

import MainRouter from './mainRouter';
import { Content } from '../layout';

const PageContainer = styled.div`
  display: flex;
  flex-grow: 1;
  min-height: 100% !important;
  max-height: 100% !important;
  position: relative;
  flex-direction: column;
  overflow: hidden;
  z-index: 1;

  @media screen and (min-width: 992px) {
    flex-direction: row;
  }
`;

// Keyboard skip link (WCAG 2.1 AA, 2.4.1 Bypass Blocks). First focusable
// element in the shell; visually hidden above the viewport until focused, then
// jumps focus to the <main id="main-content"> landmark in Content.tsx. Uses
// :root design tokens so it renders correctly in the legacy (non-`wh-ui`) shell.
const SkipLink = styled.a`
  position: absolute;
  top: 8px;
  left: 8px;
  transform: translateY(calc(-100% - 16px));
  z-index: 2000;
  padding: 8px 16px;
  border-radius: 4px;
  background: var(--bg-default);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s ease-in-out;

  &:focus {
    transform: translateY(0);
    outline: 2px solid var(--border-focus);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export { createRouteMap } from './route-config';

// The citizen sections are lazy chunks (see ./route-config.tsx).
const MainSuspenseFallback = () => (
  <div className="wh-ui flex h-full w-full items-center justify-center">
    <LoadingIndicator type="line-simple" size="md" />
  </div>
);

const Main = () => {
  return (
    <PageContainer>
      <SkipLink href="#main-content">
        <T _str="Skip to main content" />
      </SkipLink>
      <div style={{ flexShrink: 0 }}>
        <Sidebar />
      </div>
      <Content>
        <Suspense fallback={<MainSuspenseFallback />}>
          <MainRouter />
        </Suspense>
      </Content>
    </PageContainer>
  );
};

export default Main;
